import React, { useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  Divider as MuiDivider,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ClientAvatar from '_components/client-avatar/client-avatar.component'
import type { SupportMessageMetadata } from '../support-assistant.types'

type ComponentSpec = NonNullable<SupportMessageMetadata['componentSpec']>
type SendMessage = (text: string, displayText?: string) => Promise<void>

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  ACTIVE: { bg: '#dcfce7', text: '#166534' },
  SUSPENDED: { bg: '#fef9c3', text: '#854d0e' },
  CANCELED: { bg: '#fecaca', text: '#991b1b' },
  EXPIRED: { bg: '#e5e7eb', text: '#374151' },
  COMPLETED: { bg: '#dbeafe', text: '#1e40af' },
  PENDING_START_DATE: { bg: '#f3e8ff', text: '#6b21a8' },
}

const INFO_TYPE_COLORS: Record<string, string> = {
  warning: 'warning.main',
  error: 'error.main',
  success: 'success.main',
  default: 'text.primary',
}

function ClientHeader({
  name,
  email,
  phone,
  profileUrl,
  clientId,
}: {
  name: string
  email?: string
  phone?: string
  profileUrl?: string
  clientId?: string
}) {
  const navigate = useNavigate()

  const handleViewProfile = () => {
    if (profileUrl) {
      navigate(profileUrl)
    }
  }

  // Build initials from the client name (first letter of first + last name)
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: 'grey.50',
        borderRadius: 2,
        mb: 1,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      {clientId ? (
        <ClientAvatar
          clientId={clientId}
          initials={initials}
          sx={{ width: 44, height: 44, fontSize: '1rem', flexShrink: 0 }}
        />
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {name}
        </Typography>
        {email && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
          >
            {email}
          </Typography>
        )}
        {phone && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
          >
            {phone}
          </Typography>
        )}
        {profileUrl && (
          <Button
            size="small"
            variant="contained"
            onClick={handleViewProfile}
            sx={{
              fontSize: '0.75rem',
              textTransform: 'none',
              minWidth: 0,
              px: 1.5,
              py: 0.5,
              mt: 1,
            }}
          >
            View full profile
          </Button>
        )}
      </Box>
    </Box>
  )
}

function MembershipRow({
  name,
  status,
  billingAmount,
  billingCycle,
  startDate,
  endDate,
  nextBillingDate,
}: {
  name: string
  status: string
  billingAmount?: string
  billingCycle?: string
  startDate?: string
  endDate?: string
  nextBillingDate?: string
}) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.ACTIVE

  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
          {name}
        </Typography>
        <Chip
          label={status}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: colors.bg,
            color: colors.text,
            flexShrink: 0,
          }}
        />
      </Box>
      {billingAmount && (
        <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.8125rem' }}>
          {billingAmount}
          {billingCycle ? ` / ${billingCycle}` : ''}
        </Typography>
      )}
      {startDate && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          Started: {startDate}
          {endDate ? ` · Ends: ${endDate}` : ''}
        </Typography>
      )}
      {nextBillingDate && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
          Next billing: {nextBillingDate}
        </Typography>
      )}
    </Box>
  )
}

function InfoGrid({ items }: { items: Array<{ label: string; value: string; type?: string }> }) {
  return (
    <Box sx={{ p: 1.5, mb: 1 }}>
      {items.map((item, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 0.75,
            borderBottom: i < items.length - 1 ? '1px solid' : 'none',
            borderColor: 'grey.100',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {item.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: INFO_TYPE_COLORS[item.type || 'default'] || 'text.primary',
            }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

function ActionButton({
  label,
  href,
  variant = 'primary',
}: {
  label: string
  action: string
  href?: string
  variant?: string
}) {
  const navigate = useNavigate()

  const colorMap: Record<string, { bg: string; color: string }> = {
    primary: { bg: 'primary.main', color: '#fff' },
    secondary: { bg: 'transparent', color: 'text.primary' },
    danger: { bg: 'error.main', color: '#fff' },
  }
  const colors = colorMap[variant] || colorMap.primary

  return (
    <Button
      size="small"
      variant={variant === 'secondary' ? 'outlined' : 'contained'}
      color={variant === 'danger' ? 'error' : 'primary'}
      onClick={() => href && navigate(href)}
      sx={{ fontSize: '0.8125rem', textTransform: 'none', mt: 1 }}
    >
      {label}
    </Button>
  )
}

function SelectInput({
  prompt,
  contextKey,
  options,
  sendMessage,
}: {
  prompt?: string
  contextKey: string
  options: Array<{ value: string; label: string }>
  sendMessage: SendMessage
}) {
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      {options.map((opt) => (
        <Button
          key={opt.value}
          fullWidth
          variant="outlined"
          onClick={() => void sendMessage(`__input_submit:${contextKey}:${opt.value}`, opt.label)}
          sx={{
            justifyContent: 'flex-start',
            mb: 0.5,
            textTransform: 'none',
            fontSize: '0.8125rem',
            color: 'text.primary',
            borderColor: 'divider',
            bgcolor: '#fff',
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          {opt.label}
        </Button>
      ))}
    </Box>
  )
}

function DateInputGroup({
  fields,
  sendMessage,
}: {
  fields: Array<{ prompt: string; contextKey: string; placeholder?: string }>
  sendMessage: SendMessage
}) {
  const [values, setValues] = useState<Record<string, string>>({})
  const today = new Date().toISOString().split('T')[0]

  const handleSubmit = () => {
    if (fields.length === 1) {
      const field = fields[0]
      const value = values[field.contextKey]
      if (value) {
        void sendMessage(`__input_submit:${field.contextKey}:${value}`, value)
      }
    } else {
      const pairs = fields
        .filter((f) => values[f.contextKey])
        .map((f) => `${f.contextKey}:${values[f.contextKey]}`)
      if (pairs.length > 0) {
        const labels = fields.filter((f) => values[f.contextKey]).map((f) => values[f.contextKey])
        void sendMessage(`__inputs_submit:${pairs.join('|')}`, labels.join(' – '))
      }
    }
  }

  const allFilled = fields.every((f) => values[f.contextKey])

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      {fields.map((field) => (
        <Box key={field.contextKey} sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
          >
            {field.prompt}
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            inputProps={{ min: today }}
            onChange={(e) => setValues((prev) => ({ ...prev, [field.contextKey]: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ bgcolor: '#fff' }}
          />
        </Box>
      ))}
      <Button
        fullWidth
        variant="contained"
        disabled={!allFilled}
        onClick={handleSubmit}
        sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
      >
        Submit
      </Button>
    </Box>
  )
}

function DataTable({
  title,
  columns,
  rows,
  summary,
  piiRedacted,
}: {
  title?: string
  columns: Array<{ key: string; label: string; type: string }>
  rows: Array<Record<string, string>>
  summary?: string
  totalRecords?: number
  reportName?: string
  piiRedacted?: boolean
}) {
  const handleDownloadCsv = () => {
    const header = columns.map((c) => JSON.stringify(c.label)).join(',')
    const dataRows = rows.map((row) =>
      columns.map((c) => JSON.stringify(row[c.key] ?? '')).join(',')
    )
    const csv = [header, ...dataRows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${title ?? 'report'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        {title && (
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={handleDownloadCsv}
          sx={{ fontSize: '0.75rem', textTransform: 'none', ml: 'auto' }}
        >
          Download CSV
        </Button>
      </Box>
      {piiRedacted && (
        <Chip
          label="Some personal data has been redacted"
          size="small"
          color="warning"
          sx={{ mb: 1, fontSize: '0.7rem' }}
        />
      )}
      <TableContainer
        sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: 'grey.50',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i} hover>
                {columns.map((col) => (
                  <TableCell key={col.key} sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    {row[col.key] ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {summary && (
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
          {summary}
        </Typography>
      )}
    </Box>
  )
}

export function ComponentSpecRenderer({
  spec,
  sendMessage,
}: {
  spec: ComponentSpec
  sendMessage?: SendMessage
}) {
  const components = (spec as any)?.components
  if (!Array.isArray(components) || components.length === 0) return null

  return (
    <Box sx={{ mt: 1.5 }}>
      {components.map((component: any, index: number) => {
        const key = `${component.type}-${index}`
        switch (component.type) {
          case 'ClientHeader':
            return <ClientHeader key={key} {...component.props} />
          case 'MembershipRow':
            return <MembershipRow key={key} {...component.props} />
          case 'InfoGrid':
            return <InfoGrid key={key} {...component.props} />
          case 'Divider':
            return <MuiDivider key={key} sx={{ my: 1 }} />
          case 'ActionButton':
            return <ActionButton key={key} {...component.props} />
          case 'SelectInput':
            return sendMessage ? (
              <SelectInput key={key} {...component.props} sendMessage={sendMessage} />
            ) : null
          case 'DateInputGroup':
            return sendMessage ? (
              <DateInputGroup key={key} {...component.props} sendMessage={sendMessage} />
            ) : null
          case 'DataTable':
            return <DataTable key={key} {...component.props} />
          default:
            return null
        }
      })}
    </Box>
  )
}
